import { useStore } from '@nanostores/solid'
import { Show } from 'solid-js'
import type { DayCard } from '~/lib/derive-day-cards'
import type { ExtraBoxes, ExtraBoxOption } from '~/lib/derive-extra-boxes'
import { $isInSubscription } from '~/store/service-level'
import {
  CardHeader,
  CardShell,
  CtaOpened,
  CtaSkeleton,
  CtaTapToOpen,
  CtaUpgradeToOpen,
  ProgressCircle,
} from './card-parts'
import { ExtraBoxesSection } from './extra-boxes-section'

export function TodayCard(props: {
  card: DayCard
  claimingProgressId?: number | null
  extraBoxes?: ExtraBoxes
  onClaim?: (progressId: number) => void
  onUpgrade?: () => void
  onPickExtraBox?: (currency: 'coin' | 'gem', option: ExtraBoxOption) => void
}) {
  const isInSubscription = useStore($isInSubscription)
  const freeState = () => props.card.free.state
  const plusState = () => props.card.plus.state
  const isBaking = () => freeState() === 'baking'
  const isFinish = () => freeState() === 'finish'
  const circleState = () => {
    if (isBaking()) return 'in-progress'
    // 'finish' / 'opened' → arc fully drawn; otherwise (e.g. loading state
    // where deriveDayCards returns an emptyTier with state='up-next') leave
    // the ring empty so we only show the semi-transparent black backdrop.
    if (isFinish() || freeState() === 'opened') return 'complete'
    return 'empty'
  }

  const isClaiming = (id: number | null) =>
    id !== null &&
    props.claimingProgressId !== undefined &&
    props.claimingProgressId === id

  const claimFree = () => {
    if (props.card.free.progressId !== null) {
      props.onClaim?.(props.card.free.progressId)
    }
  }
  const claimPlus = () => {
    if (props.card.plus.progressId !== null) {
      props.onClaim?.(props.card.plus.progressId)
    }
  }
  // No progressIds on either tier ⇒ progresses query hasn't resolved yet.
  // Render skeleton CTAs to lock the row's footprint and avoid reflow.
  const isLoading = () =>
    props.card.free.progressId === null && props.card.plus.progressId === null

  return (
    <CardShell variant="today">
      <CardHeader
        day={props.card.day}
        isToday
        countdown={props.card.countdown}
      />

      <div class="flex items-center gap-4 self-stretch pl-3">
        <ProgressCircle
          progress={props.card.free.progress}
          goal={props.card.free.target}
          state={circleState()}
        />

        <div class="flex h-full min-w-0 flex-1 items-center justify-end self-stretch">
          <div class="flex w-full flex-col items-center justify-center gap-3">
            <Show
              when={!isLoading()}
              fallback={
                <>
                  <CtaSkeleton />
                  <CtaSkeleton />
                </>
              }
            >
              <Show
                when={freeState() === 'opened'}
                fallback={
                  <CtaTapToOpen
                    baking={isBaking()}
                    loading={isClaiming(props.card.free.progressId)}
                    onClick={isFinish() ? claimFree : undefined}
                  />
                }
              >
                <CtaOpened />
              </Show>
              <Show
                when={plusState() === 'opened'}
                fallback={
                  <CtaUpgradeToOpen
                    baking={isBaking()}
                    loading={isClaiming(props.card.plus.progressId)}
                    onUpgrade={props.onUpgrade}
                    onClaim={
                      isInSubscription() && plusState() === 'finish'
                        ? claimPlus
                        : undefined
                    }
                  />
                }
              >
                <CtaOpened />
              </Show>
            </Show>
          </div>
        </div>
      </div>

      <Show when={props.extraBoxes}>
        {(boxes) => (
          <ExtraBoxesSection
            boxes={boxes()}
            onPick={(currency, option) =>
              props.onPickExtraBox?.(currency, option)
            }
          />
        )}
      </Show>
    </CardShell>
  )
}
